import React, { useState } from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { CtaSection } from '../components/sections/CtaSection';
import { CheckIcon, SparklesIcon, RocketIcon, TrendingUpIcon, UsersIcon, BrainIcon, DollarSignIcon, TargetIcon, BuildingIcon, GraduationCapIcon, VideoIcon, CalendarIcon, ChevronDownIcon, ChevronUpIcon, ArrowRightIcon, ZapIcon, AwardIcon, TrophyIcon, ClockIcon } from 'lucide-react';
export function CreatorSummits() {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const niches = [{
    icon: <BrainIcon className="h-8 w-8" data-id="element-1373" />,
    title: 'AI & Automation Creators',
    description: 'Build AI-powered systems and workflows'
  }, {
    icon: <TrendingUpIcon className="h-8 w-8" data-id="element-1374" />,
    title: 'Marketing / Funnels / Outbound',
    description: 'Revenue generation and growth systems'
  }, {
    icon: <GraduationCapIcon className="h-8 w-8" data-id="element-1375" />,
    title: 'Course & Community Builders',
    description: 'Education products and membership models'
  }, {
    icon: <RocketIcon className="h-8 w-8" data-id="element-1376" />,
    title: 'Startup & Product Builders',
    description: 'Zero-to-one product development'
  }, {
    icon: <BuildingIcon className="h-8 w-8" data-id="element-1377" />,
    title: 'Acquisition / ETA Educators',
    description: 'Business acquisition and operations'
  }, {
    icon: <TrophyIcon className="h-8 w-8" data-id="element-1378" />,
    title: 'Performance / Leadership',
    description: 'Executive coaching and high performance'
  }];
  const phases = [{
    number: 1,
    title: 'Pre-Work',
    duration: '14 days',
    icon: <ClockIcon className="h-6 w-6" data-id="element-1379" />,
    activities: ['Onboarding and baseline assessment', 'Templates and frameworks delivered', 'Ship list and pre-work assignments', 'Community platform access']
  }, {
    number: 2,
    title: 'Cancún Build Week',
    duration: '4-5 days',
    icon: <RocketIcon className="h-6 w-6" data-id="element-1380" />,
    activities: ['Daily build sprints with clear outcomes', 'Hot seat sessions and expert feedback', 'Dedicated build blocks with support', 'Demo day and peer accountability']
  }, {
    number: 3,
    title: 'Post-Week Execution',
    duration: '30 days',
    icon: <TargetIcon className="h-6 w-6" data-id="element-1381" />,
    activities: ['Weekly execution calls', 'Live scoreboard tracking', 'Deliverables review and feedback', 'Continued community access']
  }];
  const summitThemes = [{
    title: 'AI Operator Summit',
    tagline: 'Build your AI-powered business engine',
    outcomes: ['Custom AI workflow automation', 'Prompt library and SOPs', 'AI agent deployment plan', 'ROI measurement dashboard'],
    deliverables: 'Working AI system + 90-day rollout plan'
  }, {
    title: 'Offer-to-Revenue Summit',
    tagline: 'Ship a validated offer in 5 days',
    outcomes: ['Offer positioning and messaging', 'Sales page and funnel architecture', 'Outbound campaign ready to launch', 'First 10 customer acquisition plan'],
    deliverables: 'Live offer + first sales within 30 days'
  }, {
    title: 'Acquisition Deal Lab',
    tagline: 'Source and structure your first deal',
    outcomes: ['Deal sourcing playbook', 'Financial model and valuation', 'LOI template and negotiation framework', 'Due diligence checklist'],
    deliverables: 'Active pipeline + deal structure ready'
  }, {
    title: 'Creator Business OS Summit',
    tagline: 'Launch your Skool community in 5 days',
    outcomes: ['Complete Skool curriculum architecture', 'Onboarding sequence and challenges', 'Engagement scoreboard and gamification', '30-day content calendar'],
    deliverables: 'Live Skool community + first cohort plan'
  }];
  const weHandle = ['Venue & resort coordination', 'Agenda/curriculum architecture', 'Faculty + facilitation support', 'Ops + attendee communications', 'Digital platform journey (pre/post)', 'Templates + playbooks', 'Production/content capture support', 'Payments + checkout', 'Support team coordination'];
  const youHandle = ['Promotion to your audience', '2-3 pre-event live sessions', 'Welcome session + closing session', 'Community hype + recruitment', 'Co-host collaboration calls'];
  const proofCases = [{
    title: 'AI Automation Summit',
    metrics: {
      seats: 42,
      completion: '95%',
      deliverables: '38 shipped'
    },
    testimonial: '"Built and deployed 3 AI agents in 4 days. The structure and support made it actually happen."',
    author: 'Summit Attendee'
  }, {
    title: 'Offer Launch Sprint',
    metrics: {
      seats: 35,
      completion: '89%',
      deliverables: '31 live offers'
    },
    testimonial: '"Went from idea to first sale in 18 days. The accountability and frameworks were game-changing."',
    author: 'Summit Attendee'
  }, {
    title: 'Creator Business OS',
    metrics: {
      seats: 28,
      completion: '93%',
      deliverables: '24 communities launched'
    },
    testimonial: '"Launched my Skool community with 50 founding members. The curriculum template saved me months."',
    author: 'Summit Attendee'
  }];
  const faqs = [{
    question: 'What audience size works best?',
    answer: 'Ideal partners have 5,000+ engaged followers/subscribers and proven ability to drive action. We prioritize engagement quality over raw numbers.'
  }, {
    question: 'Can multiple creators co-host?',
    answer: 'Yes! We typically bring together 3-5 aligned creators per summit. This creates a bigger, more valuable experience than any single brand could deliver alone.'
  }, {
    question: "What's included in the $8,000 seat?",
    answer: '4-5 days in Cancún (accommodation, meals, venue), pre-work curriculum, build week facilitation, templates/playbooks, 30-day post-event execution program, and lifetime community access.'
  }, {
    question: 'Can we customize the agenda?',
    answer: 'Yes. We start with proven frameworks for your chosen theme, then customize based on your audience needs and co-host expertise. You have input on curriculum design.'
  }, {
    question: 'What are the next dates?',
    answer: 'We run summits quarterly. Dates are set 90 days in advance once creator partners are confirmed. Book a partner call to see upcoming availability.'
  }, {
    question: 'How do tracked links work?',
    answer: 'Each creator partner gets unique UTM-tracked links. We provide a real-time dashboard showing your referrals, conversions, and revenue share. Full transparency.'
  }, {
    question: "What's the minimum seat threshold?",
    answer: 'Each creator partner commits to driving a minimum of 8-12 seats (varies by summit theme). This ensures quality cohort size and protects the experience.'
  }, {
    question: 'What does the application process look like?',
    answer: '1) Submit application, 2) 15-min partner call, 3) Review partner pack and economics, 4) Alignment call with other co-hosts, 5) Contract and promotion kickoff.'
  }, {
    question: 'Do you handle travel logistics?',
    answer: 'Attendees book their own flights to Cancún. We handle everything from airport pickup to departure: accommodation, meals, venue, activities, and ground transportation.'
  }, {
    question: 'What makes this different from retreats/masterminds?',
    answer: 'This is executive-education caliber curriculum with real build time and deliverables. Not networking or "vibes." Attendees ship tangible outcomes with structured support before, during, and after.'
  }];
  return <div className="pt-20 bg-[#F4E8D0]/30" data-id="element-1382">
      {/* Hero */}
      <section className="relative py-24 md:py-32 bg-gradient-to-br from-[#374047] via-[#374047] to-[#0099A1] overflow-hidden" data-id="element-1383">
        <div className="absolute inset-0 opacity-10" data-id="element-1384">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} data-id="element-1385"></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-1386">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1387">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" data-id="element-1388">
              Co-Host a Sold-Out Creator Summit in Cancún
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed" data-id="element-1389">
              You bring the audience. We bring the venue, curriculum engine,
              production, and delivery—so your community ships real outcomes in
              4–5 days.
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-10" data-id="element-1390">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20" data-id="element-1391">
                <div className="text-3xl font-bold text-white mb-2" data-id="element-1392">$8,000</div>
                <div className="text-white/80 text-sm" data-id="element-1393">
                  per seat premium experience
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20" data-id="element-1394">
                <div className="text-3xl font-bold text-white mb-2" data-id="element-1395">3-5</div>
                <div className="text-white/80 text-sm" data-id="element-1396">
                  aligned creators co-hosting
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20" data-id="element-1397">
                <div className="text-3xl font-bold text-white mb-2" data-id="element-1398">
                  Pre + Post
                </div>
                <div className="text-white/80 text-sm" data-id="element-1399">
                  digital journey for lasting results
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12" data-id="element-1400">
              <Button variant="primary" size="lg" to="/contact" className="bg-[#0099A1] text-white hover:bg-[#006D73] border-0" data-id="element-1401">
                Apply to Co-Host
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-1402" />
              </Button>
              <Button variant="secondary" size="lg" to="/contact" className="bg-white/20 backdrop-blur-sm border-2 border-white text-white hover:bg-white/30" data-id="element-1403">
                Book a 15-min Partner Call
              </Button>
            </div>

            <div className="flex flex-col items-center gap-4" data-id="element-1404">
              <p className="text-white/70 text-sm" data-id="element-1405">
                Executive education experience at top-tier institutions
              </p>
              <div className="flex flex-wrap justify-center gap-6 opacity-60" data-id="element-1406">
                <div className="text-white font-semibold" data-id="element-1407">INSEAD</div>
                <div className="text-white font-semibold" data-id="element-1408">Wharton</div>
                <div className="text-white font-semibold" data-id="element-1409">MIT</div>
                <div className="text-white font-semibold" data-id="element-1410">Stanford</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 bg-white" data-id="element-1411">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1412">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1413">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1414">
              Who This Is For
            </h2>
            <p className="text-lg text-[#374047]/70" data-id="element-1415">
              Creator niches that thrive in high-accountability build
              environments
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-1416">
            {niches.map((niche, index) => <Card key={index} className="border-2 border-[#0099A1]/20 hover:border-[#0099A1]/40 transition-all hover:shadow-lg" data-id="element-1417">
                <CardBody className="p-8" data-id="element-1418">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#0099A1] to-[#006D73] rounded-2xl flex items-center justify-center text-white mb-6" data-id="element-1419">
                    {niche.icon}
                  </div>
                  <h3 className="text-xl font-bold text-[#374047] mb-3" data-id="element-1420">
                    {niche.title}
                  </h3>
                  <p className="text-[#374047]/70" data-id="element-1421">{niche.description}</p>
                </CardBody>
              </Card>)}
          </div>
        </div>
      </section>

      {/* The Format */}
      <section className="py-20 bg-[#F4E8D0]/30" data-id="element-1422">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1423">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1424">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1425">
              The Format: 3 Phases
            </h2>
            <p className="text-lg text-[#374047]/70" data-id="element-1426">
              Structured journey from preparation to execution
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-1427">
            {phases.map((phase, index) => <div key={index} className="relative" data-id="element-1428">
                <Card className="border-2 border-[#0099A1]/30 h-full" data-id="element-1429">
                  <CardHeader className="bg-gradient-to-br from-[#0099A1] to-[#006D73] text-white p-8" data-id="element-1430">
                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-3xl font-bold mb-4" data-id="element-1431">
                      {phase.number}
                    </div>
                    <h3 className="text-2xl font-bold mb-2" data-id="element-1432">{phase.title}</h3>
                    <div className="flex items-center gap-2 text-sm bg-white/20 rounded-lg px-3 py-2 w-fit" data-id="element-1433">
                      {phase.icon}
                      <span data-id="element-1434">{phase.duration}</span>
                    </div>
                  </CardHeader>
                  <CardBody className="p-8" data-id="element-1435">
                    <ul className="space-y-4" data-id="element-1436">
                      {phase.activities.map((activity, i) => <li key={i} className="flex items-start gap-3" data-id="element-1437">
                          <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1438" />
                          <span className="text-[#374047]" data-id="element-1439">{activity}</span>
                        </li>)}
                    </ul>
                  </CardBody>
                </Card>
                {index < phases.length - 1 && <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-[#0099A1]/30" data-id="element-1440"></div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Summit Themes */}
      <section className="py-20 bg-white" data-id="element-1441">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1442">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1443">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1444">
              Summit Themes: Choose One
            </h2>
            <p className="text-lg text-[#374047]/70" data-id="element-1445">
              Each theme has proven curriculum and clear deliverables
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto" data-id="element-1446">
            {summitThemes.map((theme, index) => <Card key={index} className="border-2 border-[#0099A1]/30 hover:border-[#0099A1] transition-all hover:shadow-xl" data-id="element-1447">
                <CardHeader className="bg-[#F4E8D0]/50 p-8 border-b-2 border-[#0099A1]/20" data-id="element-1448">
                  <h3 className="text-2xl font-bold text-[#374047] mb-2" data-id="element-1449">
                    {theme.title}
                  </h3>
                  <p className="text-[#374047]/70 text-lg" data-id="element-1450">{theme.tagline}</p>
                </CardHeader>
                <CardBody className="p-8" data-id="element-1451">
                  <h4 className="font-bold text-[#374047] mb-4" data-id="element-1452">
                    Key Outcomes:
                  </h4>
                  <ul className="space-y-3 mb-6" data-id="element-1453">
                    {theme.outcomes.map((outcome, i) => <li key={i} className="flex items-start gap-3" data-id="element-1454">
                        <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1455" />
                        <span className="text-[#374047]" data-id="element-1456">{outcome}</span>
                      </li>)}
                  </ul>
                  <div className="bg-[#0099A1]/10 rounded-xl p-4 border-l-4 border-[#0099A1]" data-id="element-1457">
                    <p className="font-semibold text-[#374047]" data-id="element-1458">
                      <strong data-id="element-1459">Deliverables:</strong> {theme.deliverables}
                    </p>
                  </div>
                </CardBody>
              </Card>)}
          </div>
        </div>
      </section>

      {/* What We Handle vs You Handle */}
      <section className="py-20 bg-[#F4E8D0]/30" data-id="element-1460">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1461">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1462">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1463">
              Division of Responsibilities
            </h2>
            <p className="text-lg text-[#374047]/70" data-id="element-1464">
              Clear roles so you can focus on what you do best
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto" data-id="element-1465">
            <Card className="border-2 border-[#0099A1]/30" data-id="element-1466">
              <CardHeader className="bg-gradient-to-br from-[#0099A1] to-[#006D73] text-white p-8" data-id="element-1467">
                <h3 className="text-2xl font-bold" data-id="element-1468">What We Handle</h3>
              </CardHeader>
              <CardBody className="p-8" data-id="element-1469">
                <ul className="space-y-4" data-id="element-1470">
                  {weHandle.map((item, index) => <li key={index} className="flex items-start gap-3" data-id="element-1471">
                      <CheckIcon className="h-6 w-6 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1472" />
                      <span className="text-[#374047] text-lg" data-id="element-1473">{item}</span>
                    </li>)}
                </ul>
              </CardBody>
            </Card>

            <Card className="border-2 border-[#FF6B65]/30" data-id="element-1474">
              <CardHeader className="bg-gradient-to-br from-[#FF6B65] to-[#FF8B85] text-white p-8" data-id="element-1475">
                <h3 className="text-2xl font-bold" data-id="element-1476">What You Handle</h3>
              </CardHeader>
              <CardBody className="p-8" data-id="element-1477">
                <ul className="space-y-4" data-id="element-1478">
                  {youHandle.map((item, index) => <li key={index} className="flex items-start gap-3" data-id="element-1479">
                      <CheckIcon className="h-6 w-6 text-[#FF6B65] flex-shrink-0 mt-0.5" data-id="element-1480" />
                      <span className="text-[#374047] text-lg" data-id="element-1481">{item}</span>
                    </li>)}
                </ul>
              </CardBody>
            </Card>
          </div>
        </div>
      </section>

      {/* Partner Economics */}
      <section className="py-20 bg-white" data-id="element-1482">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1483">
          <div className="max-w-4xl mx-auto" data-id="element-1484">
            <div className="text-center mb-12" data-id="element-1485">
              <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1486">
                Partner Economics
              </h2>
              <p className="text-lg text-[#374047]/70" data-id="element-1487">
                Simple, transparent revenue share model
              </p>
            </div>

            <Card className="border-2 border-[#0099A1]/30 shadow-xl" data-id="element-1488">
              <CardBody className="p-10" data-id="element-1489">
                <div className="space-y-6" data-id="element-1490">
                  <div className="flex items-start gap-4" data-id="element-1491">
                    <div className="w-12 h-12 bg-[#0099A1]/10 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1492">
                      <DollarSignIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1493" />
                    </div>
                    <div data-id="element-1494">
                      <h4 className="font-bold text-[#374047] mb-2 text-xl" data-id="element-1495">
                        Revenue Share on Direct Seats
                      </h4>
                      <p className="text-[#374047]/70 text-lg" data-id="element-1496">
                        Earn revenue share on every seat you drive through your
                        tracked links. Full transparency with real-time
                        dashboard.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-id="element-1497">
                    <div className="w-12 h-12 bg-[#0099A1]/10 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1498">
                      <CalendarIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1499" />
                    </div>
                    <div data-id="element-1500">
                      <h4 className="font-bold text-[#374047] mb-2 text-xl" data-id="element-1501">
                        Weekly Pipeline Check-ins
                      </h4>
                      <p className="text-[#374047]/70 text-lg" data-id="element-1502">
                        Regular sync calls to review progress, optimize
                        messaging, and ensure we hit seat targets together.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-id="element-1503">
                    <div className="w-12 h-12 bg-[#0099A1]/10 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1504">
                      <TargetIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1505" />
                    </div>
                    <div data-id="element-1506">
                      <h4 className="font-bold text-[#374047] mb-2 text-xl" data-id="element-1507">
                        Minimum Seat Threshold
                      </h4>
                      <p className="text-[#374047]/70 text-lg" data-id="element-1508">
                        Each partner commits to 8-12 seats minimum (varies by
                        theme) to ensure quality cohort size.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-10 pt-8 border-t-2 border-gray-100 text-center" data-id="element-1509">
                  <Button variant="primary" to="/contact" size="lg" data-id="element-1510">
                    Request the Full Partner Pack (PDF)
                  </Button>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </section>

      {/* Proof */}
      <section className="py-20 bg-[#F4E8D0]/30" data-id="element-1511">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1512">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1513">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1514">
              Proven Results
            </h2>
            <p className="text-lg text-[#374047]/70" data-id="element-1515">
              Real outcomes from past summits
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-1516">
            {proofCases.map((proof, index) => <Card key={index} className="border-2 border-[#0099A1]/20" data-id="element-1517">
                <CardHeader className="bg-white p-8 border-b-2 border-gray-100" data-id="element-1518">
                  <h3 className="text-xl font-bold text-[#374047] mb-6" data-id="element-1519">
                    {proof.title}
                  </h3>
                  <div className="grid grid-cols-3 gap-4" data-id="element-1520">
                    <div className="text-center" data-id="element-1521">
                      <div className="text-2xl font-bold text-[#0099A1]" data-id="element-1522">
                        {proof.metrics.seats}
                      </div>
                      <div className="text-xs text-[#374047]/60" data-id="element-1523">Seats</div>
                    </div>
                    <div className="text-center" data-id="element-1524">
                      <div className="text-2xl font-bold text-[#0099A1]" data-id="element-1525">
                        {proof.metrics.completion}
                      </div>
                      <div className="text-xs text-[#374047]/60" data-id="element-1526">
                        Completion
                      </div>
                    </div>
                    <div className="text-center" data-id="element-1527">
                      <div className="text-2xl font-bold text-[#0099A1]" data-id="element-1528">
                        {proof.metrics.deliverables}
                      </div>
                      <div className="text-xs text-[#374047]/60" data-id="element-1529">Shipped</div>
                    </div>
                  </div>
                </CardHeader>
                <CardBody className="p-8" data-id="element-1530">
                  <p className="text-[#374047] italic mb-4" data-id="element-1531">
                    {proof.testimonial}
                  </p>
                  <p className="text-[#374047]/60 text-sm" data-id="element-1532">— {proof.author}</p>
                </CardBody>
              </Card>)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white" data-id="element-1533">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1534">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1535">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1536">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-4" data-id="element-1537">
            {faqs.map((faq, index) => <Card key={index} className="border-2 border-gray-200 hover:border-[#0099A1]/40 transition-colors" data-id="element-1538">
                <CardBody className="p-6" data-id="element-1539">
                  <button onClick={() => setExpandedFaq(expandedFaq === index ? null : index)} className="w-full flex items-center justify-between text-left" data-id="element-1540">
                    <h4 className="font-bold text-[#374047] text-lg pr-4" data-id="element-1541">
                      {faq.question}
                    </h4>
                    {expandedFaq === index ? <ChevronUpIcon className="h-6 w-6 text-[#0099A1] flex-shrink-0" data-id="element-1542" /> : <ChevronDownIcon className="h-6 w-6 text-[#374047]/40 flex-shrink-0" data-id="element-1543" />}
                  </button>
                  {expandedFaq === index && <p className="text-[#374047]/70 mt-4 leading-relaxed" data-id="element-1544">
                      {faq.answer}
                    </p>}
                </CardBody>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-br from-[#374047] via-[#374047] to-[#0099A1]" data-id="element-1545">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1546">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1547">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-1548">
              Let's Build the Next Flagship Summit Together
            </h2>
            <p className="text-xl text-white/90 mb-10" data-id="element-1549">
              Limited creator partners per quarter to protect quality
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1550">
              <Button variant="primary" size="lg" to="/contact" className="bg-[#0099A1] text-white hover:bg-[#006D73] border-0" data-id="element-1551">
                Apply to Co-Host
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-1552" />
              </Button>
              <Button variant="secondary" size="lg" to="/contact" className="bg-white/20 backdrop-blur-sm border-2 border-white text-white hover:bg-white/30" data-id="element-1553">
                Book a 15-min Partner Call
              </Button>
            </div>
          </div>
        </div>
      </section>

      <CtaSection data-id="element-1554" />
    </div>;
}